import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import ProductMediaManager from "../../admin/ProductMediaManager";
import { api } from "../../api/client";

const emptyForm = {
    name: "",
    generic_name: "",
    brand: "",
    category_id: "",
    mrp: "",
    selling_price: "",
    pack_size: "",
    prescription_required: false,
    stock_qty: 0,
    images: "",
    short_description: "",
    tags: "",
    active: true,
};

const Content = () => {
    const { id } = useParams();
    const isNew = id === "new";
    const navigate = useNavigate();
    const [form, setForm] = useState(emptyForm);
    const [productData, setProductData] = useState(null);  // full product object for Media Manager
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(!isNew);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            const cats = (await api.get("/categories")).data;
            setCategories(cats);
            if (!isNew) {
                try {
                    // Load via admin products list (we have detail endpoint via patch; use products GET)
                    const r = await api.get("/admin/products", { params: { q: "", page_size: 100 } });
                    // Better: use direct product GET (auth not required, but it filters active==true)
                    const direct = await api.get(`/products/${id}`).catch(() => null);
                    const p = direct?.data || (r.data.items || []).find((x) => x.id === id);
                    if (!p) {
                        toast.error("Product not found");
                        navigate("/admin/products");
                        return;
                    }
                    setProductData(p);  // v3B — for the Media Manager
                    setForm({
                        name: p.name || "",
                        generic_name: p.generic_name || "",
                        brand: p.brand || "",
                        category_id: p.category_id || (cats[0]?.id || ""),
                        mrp: String(p.mrp ?? ""),
                        selling_price: String(p.selling_price ?? ""),
                        pack_size: p.pack_size || "",
                        prescription_required: !!p.prescription_required,
                        stock_qty: p.stock_qty ?? 0,
                        // v3B — image objects now; legacy text-box only shows legacy strings if any.
                        images: (p.images || [])
                            .filter((it) => typeof it === "string" || it?.is_legacy)
                            .map((it) => (typeof it === "string" ? it : it.url))
                            .join("\n"),
                        short_description: p.short_description || "",
                        tags: (p.tags || []).join(", "),
                        active: p.active !== false,
                    });
                } finally {
                    setLoading(false);
                }
            } else {
                setForm((f) => ({ ...f, category_id: cats[0]?.id || "" }));
            }
        })();
    }, [id, isNew, navigate]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        const mrp = parseFloat(form.mrp);
        const sp = parseFloat(form.selling_price);
        if (!form.name.trim() || !form.category_id || !(mrp > 0) || !(sp > 0)) {
            toast.error("Name, category, MRP and selling price are required");
            return;
        }
        if (sp > mrp) {
            toast.error("Selling price must be ≤ MRP");
            return;
        }
        const payload = {
            name: form.name.trim(),
            generic_name: form.generic_name.trim(),
            brand: form.brand.trim(),
            category_id: form.category_id,
            mrp,
            selling_price: sp,
            pack_size: form.pack_size.trim(),
            prescription_required: !!form.prescription_required,
            stock_qty: parseInt(form.stock_qty || "0", 10),
            images: form.images.split(/\n/).map((s) => s.trim()).filter(Boolean),
            short_description: form.short_description.trim(),
            tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
            active: form.active,
        };
        setBusy(true);
        try {
            if (isNew) {
                const r = await api.post("/admin/products", payload);
                toast.success("Product created");
                navigate(`/admin/products/${r.data.id}/edit`);
            } else {
                await api.patch(`/admin/products/${id}`, payload);
                toast.success("Saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        if (!window.confirm("Disable this product? It will be hidden from the customer catalog. Existing orders remain unaffected.")) return;
        setBusy(true);
        try {
            await api.delete(`/admin/products/${id}`);
            toast.success("Product disabled");
            navigate("/admin/products");
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Disable failed");
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <div className="h-[60vh] skeleton rounded-2xl" />;

    return (
        <form onSubmit={submit} className="space-y-5" data-testid="admin-product-edit">
            <div className="flex items-center justify-between">
                <Link to="/admin/products" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-700">
                    <ArrowLeft className="h-4 w-4" /> All products
                </Link>
                <div className="flex gap-2">
                    {!isNew && (
                        <button type="button" onClick={disable} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-4 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50" data-testid="product-disable-btn">
                            <Trash2 className="h-3.5 w-3.5" /> Disable
                        </button>
                    )}
                    <button type="submit" disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50" data-testid="product-save-btn">
                        <Save className="h-3.5 w-3.5" /> {busy ? "Saving..." : (isNew ? "Create product" : "Save changes")}
                    </button>
                </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-3">
                <section className="lg:col-span-3 space-y-5">
                    {!isNew && productData && (
                        <ProductMediaManager product={productData} onUpdate={setProductData} />
                    )}
                </section>
                <section className="lg:col-span-2 space-y-5">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <p className="label-uppercase">Basics</p>
                        <h3 className="text-sm font-semibold text-slate-900">Product identity</h3>
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <label className="col-span-2 block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Name *</span>
                                <input value={form.name} onChange={set("name")} required className="input-clean !rounded-lg mt-1" data-testid="product-name" />
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Brand</span>
                                <input value={form.brand} onChange={set("brand")} className="input-clean !rounded-lg mt-1" data-testid="product-brand" />
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Generic name</span>
                                <input value={form.generic_name} onChange={set("generic_name")} className="input-clean !rounded-lg mt-1" data-testid="product-generic" />
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Category *</span>
                                <select value={form.category_id} onChange={set("category_id")} required className="input-clean !rounded-lg mt-1" data-testid="product-category">
                                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Pack size</span>
                                <input value={form.pack_size} onChange={set("pack_size")} className="input-clean !rounded-lg mt-1" placeholder="e.g. 10 tablets" data-testid="product-pack-size" />
                            </label>
                            <label className="col-span-2 block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Short description</span>
                                <textarea value={form.short_description} onChange={set("short_description")} rows={3} className="input-clean !rounded-lg mt-1" data-testid="product-description" />
                            </label>
                            <label className="col-span-2 block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Tags (comma-separated)</span>
                                <input value={form.tags} onChange={set("tags")} className="input-clean !rounded-lg mt-1" data-testid="product-tags" />
                            </label>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <p className="label-uppercase">Images</p>
                        <h3 className="text-sm font-semibold text-slate-900">Image URLs (one per line)</h3>
                        <textarea value={form.images} onChange={set("images")} rows={4} className="input-clean !rounded-lg mt-3" placeholder="https://..." data-testid="product-images" />
                    </div>
                </section>

                <aside className="space-y-5">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <p className="label-uppercase">Pricing</p>
                        <div className="mt-3 space-y-3">
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">MRP (₹) *</span>
                                <input type="number" min="0" step="0.01" value={form.mrp} onChange={set("mrp")} required className="input-clean !rounded-lg mt-1" data-testid="product-mrp" />
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Selling price (₹) *</span>
                                <input type="number" min="0" step="0.01" value={form.selling_price} onChange={set("selling_price")} required className="input-clean !rounded-lg mt-1" data-testid="product-selling-price" />
                            </label>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <p className="label-uppercase">Inventory</p>
                        <label className="mt-3 block">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Stock quantity</span>
                            <input type="number" min="0" value={form.stock_qty} onChange={set("stock_qty")} className="input-clean !rounded-lg mt-1" data-testid="product-stock" />
                        </label>
                        {!isNew && <p className="mt-2 text-[11px] text-slate-500">Use the "Restock" button on the list page to log a batch with expiry.</p>}
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <p className="label-uppercase">Compliance</p>
                        <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 hover:border-brand-200">
                            <input type="checkbox" checked={form.prescription_required} onChange={set("prescription_required")} className="mt-0.5 accent-brand-700" data-testid="product-rx-required" />
                            <div>
                                <p className="flex items-center gap-1 text-sm font-medium text-slate-900"><ShieldCheck className="h-3.5 w-3.5 text-brand-700" /> Rx required</p>
                                <p className="text-[11px] text-slate-500">Customers must attach a verified prescription.</p>
                            </div>
                        </label>
                        <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 hover:border-brand-200">
                            <input type="checkbox" checked={form.active} onChange={set("active")} className="mt-0.5 accent-brand-700" data-testid="product-active" />
                            <div>
                                <p className="text-sm font-medium text-slate-900">Active</p>
                                <p className="text-[11px] text-slate-500">Visible in the customer catalog.</p>
                            </div>
                        </label>
                    </div>
                </aside>
            </div>
        </form>
    );
};

const AdminProductEdit = () => (
    <AdminAuthGuard requireAdmin>
        <AdminLayout title="Edit product" subtitle="Edit catalog details and stock">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminProductEdit;
